<template>
  <div ref="root" class="dish-temperature">
    <div class="section-header">
      <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M14 14.76V5a2 2 0 0 0-4 0v9.76a4 4 0 1 0 4 0z"></path>
      </svg>
      <h3>{{ title || '菜品中心温度' }}</h3>
      <div v-if="stats.length" class="section-stats">
        <span v-for="(item, index) in stats" :key="index" class="stat-item">
          <span class="stat-label">{{ item.label }}</span>
          <span class="stat-value">{{ item.value }}</span>
        </span>
      </div>
    </div>

    <div ref="scrollContainer" class="group-list">
      <div v-for="group in visibleGroups" :key="group.key" class="meal-group">
        <div class="meal-head">
          <span class="meal-bar"></span>
          <span class="meal-name">{{ group.label }}</span>
          <span class="meal-count">{{ group.records.length }} 道</span>
        </div>

        <div class="dish-grid" :style="gridStyle">
          <div v-for="(record, index) in group.records" :key="index" class="dish-card">
            <div class="dish-info">
              <div class="dish-name" :title="record.dish">{{ record.dish }}</div>
              <div class="dish-temp" :class="{ low: record.temperature < 60 }">
                {{ formatTemp(record.temperature) }}
              </div>
            </div>
            <div class="dish-thumb">
              <svg class="thumb-placeholder" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <circle cx="12" cy="12" r="8"></circle>
                <circle cx="12" cy="12" r="3.5"></circle>
              </svg>
              <img
                v-if="record.image_url"
                :src="record.image_url"
                class="dish-image"
                @error="$event.target.style.display = 'none'"
              />
            </div>
          </div>
        </div>
      </div>

      <div v-if="!visibleGroups.length" class="empty">暂无温度记录</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DishTemperature',
  props: {
    title: {
      type: String,
      default: ''
    },
    groups: {
      type: Array,
      default: () => []
    },
    stats: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      columns: 1,
      resizeObserver: null
    }
  },
  computed: {
    visibleGroups() {
      return this.groups.filter(group => group.records && group.records.length)
    },
    gridStyle() {
      return {
        gridTemplateColumns: `repeat(${this.columns}, minmax(0, 1fr))`
      }
    }
  },
  mounted() {
    this.updateColumns()
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.updateColumns)
      this.resizeObserver.observe(this.$el)
    } else {
      window.addEventListener('resize', this.updateColumns)
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    } else {
      window.removeEventListener('resize', this.updateColumns)
    }
    if (this.scrollTimer) {
      clearInterval(this.scrollTimer)
      this.scrollTimer = null
    }
  },
  methods: {
    updateColumns() {
      const width = this.$el ? this.$el.clientWidth : 0
      this.columns = width >= 420 ? 2 : 1
    },
    formatTemp(value) {
      return `${Number(value || 0).toFixed(1)}℃`
    },
    startAutoScroll() {
      const container = this.$refs.scrollContainer
      if (!container) return

      const maxScroll = container.scrollHeight - container.clientHeight
      if (maxScroll <= 0) return

      let scrollTop = 0
      if (this.scrollTimer) {
        clearInterval(this.scrollTimer)
      }
      this.scrollTimer = setInterval(() => {
        scrollTop += 1
        if (scrollTop >= maxScroll) {
          scrollTop = 0
          container.scrollTop = 0
        } else {
          container.scrollTop = scrollTop
        }
      }, 40)
    }
  }
}
</script>

<style scoped>
.dish-temperature {
  background: rgba(26, 54, 93, 0.6);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(99, 179, 237, 0.2);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(99, 179, 237, 0.2);
  flex-shrink: 0;
}

.icon {
  width: 20px;
  height: 20px;
  color: #63b3ed;
}

h3 {
  font-size: 16px;
  font-weight: 600;
  color: #e2e8f0;
  margin: 0;
}

.section-stats {
  display: flex;
  align-items: baseline;
  gap: 14px;
  margin-left: auto;
}

.stat-item {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.stat-label {
  font-size: 12px;
  color: #a0aec0;
}

.stat-value {
  font-size: 14px;
  font-weight: 600;
  color: #63b3ed;
}

.group-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.meal-group {
  padding-bottom: 12px;
}

.meal-group + .meal-group {
  border-top: 1px dashed rgba(99, 179, 237, 0.14);
  padding-top: 12px;
}

.meal-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.meal-bar {
  width: 3px;
  height: 14px;
  border-radius: 2px;
  background: #63b3ed;
  box-shadow: 0 0 6px rgba(99, 179, 237, 0.6);
}

.meal-name {
  font-size: 14px;
  font-weight: 600;
  color: #e2e8f0;
}

.meal-count {
  font-size: 12px;
  color: #a0aec0;
}

.dish-grid {
  display: grid;
  gap: 8px;
}

.dish-card {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 10px;
  background: rgba(16, 38, 70, 0.85);
  border: 1px solid rgba(99, 179, 237, 0.18);
  border-radius: 8px;
}

.dish-info {
  flex: 1;
  min-width: 0;
}

.dish-name {
  font-size: 14px;
  font-weight: 600;
  color: #e2e8f0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dish-temp {
  margin-top: 6px;
  font-size: 20px;
  font-weight: 700;
  font-family: "Bahnschrift", "DIN Alternate", sans-serif;
  color: #48bb78;
}

.dish-temp.low {
  color: #fc8181;
}

.dish-thumb {
  position: relative;
  flex: 0 0 56px;
  width: 56px;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(49, 130, 206, 0.12);
  border: 1px solid rgba(99, 179, 237, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(160, 174, 192, 0.5);
}

.thumb-placeholder {
  width: 26px;
  height: 26px;
}

.dish-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.empty {
  padding: 8px 10px;
  font-size: 12px;
  color: #718096;
  background: rgba(16, 38, 70, 0.35);
  border-radius: 8px;
}
</style>
